import { FC, Fragment, useMemo } from 'react';
import { ICarDataItem, ICarSingleVariant, ICarVarianItem, ITalentCars } from 'shared/interfaces/ICars';
import { useUploadImage } from '../hooks/useUploadImage';
import { useCarForm } from '../hooks/useCarForm';
import { FormModeProps } from 'shared/interfaces/IModalForm';
import { CarsIcon } from '@room-match/shared-ui-components';
import { HandleAddItemProps, HandleEditItemProps } from 'shared/hooks/PetsCars/useSubmitForm';
import ModalForm from 'components/MyInfo/ModalForm/ModalForm';
import CarForm from './CarForm/CarForm';

type ItemProps = {
  manufacturer: ICarDataItem;
  model: ICarDataItem;
  variant: ICarVarianItem;
  singleVariant: ICarSingleVariant;
};

type CarAddEditModalProps = {
  data?: ITalentCars | undefined;
  mode: FormModeProps;
  openModal: boolean;
  handleOpenModal: VoidFunction;
  handleAddCar: (value: HandleAddItemProps<ItemProps>) => void;
  handleEditCar: (value: HandleEditItemProps<ItemProps>) => void;
};

const CarAddEditModal: FC<CarAddEditModalProps> = ({
  openModal,
  handleOpenModal,
  mode,
  data,
  handleAddCar,
  handleEditCar,
}) => {
  const {
    handleRemoveImages,
    handleOnDropImage,
    uploadedFiles,
    filesToBeSaved,
    handleFilesToBeSaved,
    handleOnDragEnd,
    NotificationModal,
    isOpen,
  } = useUploadImage({ current_saved_images: mode === 'edit' && data ? data?.attached_media || null : [] });
  const { carForms, selectedMake, selectedModel, selectedVariant, selectedSingleVariant } = useCarForm(
    data || undefined,
  );
  const isMakeModelVariantsSelected = useMemo(
    () => Boolean(selectedMake && selectedModel && selectedVariant && selectedSingleVariant),
    [selectedMake, selectedModel, selectedVariant, selectedSingleVariant],
  );

  const handleSave = async () => {
    handleOpenModal();

    const carData = {
      manufacturer: selectedMake as ICarDataItem,
      model: selectedModel as ICarDataItem,
      variant: selectedVariant as ICarVarianItem,
      singleVariant: selectedSingleVariant as ICarSingleVariant,
    };

    if (isMakeModelVariantsSelected) {
      if (mode === 'add') {
        handleAddCar({
          filesToBeSaved: filesToBeSaved,
          items: carData,
        });
      } else if (mode === 'edit') {
        handleEditCar({
          id: data?.id || '',
          items: carData,
          filesToBeSaved,
        });
      }
    }
  };

  return (
    <Fragment>
      {isOpen ? <NotificationModal /> : null}
      <ModalForm
        handleOnDragEnd={handleOnDragEnd}
        headerIcon={<CarsIcon fontSize="inherit" />}
        filesToBeSaved={filesToBeSaved}
        handleFilesToBeSaved={(newFile) => handleFilesToBeSaved(newFile)}
        uploadedFiles={uploadedFiles}
        openModal={openModal}
        handleOpenModal={() => {
          handleOpenModal();
        }}
        handleUploadImages={(file: File[]) => {
          handleOnDropImage(file);
        }}
        handleRemoveImages={handleRemoveImages}
        title={mode === 'add' ? 'Add Your Car' : 'Edit Your Car'}
        formComponent={
          <CarForm
            onSave={handleSave}
            disableSaveButton={!isMakeModelVariantsSelected || filesToBeSaved?.length > 5}
            carForms={carForms}
            imageLength={filesToBeSaved?.length}
          />
        }
      />
    </Fragment>
  );
};

export default CarAddEditModal;
